import { cva } from 'class-variance-authority'
import { clsx } from 'clsx'
import { PropertyValueMap, unsafeCSS } from 'lit'
import { property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html, unsafeStatic } from 'lit/static-html.js'

import { anchorClass } from '@/common/const'

import { Common, Size } from '../../common/types'
import { DdsElement } from '../../internal/dds-hu-element'
import styles from './_styles/typography.scss?inline'

export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'body'
  | 'caption'
  | 'description'

export type TypographySize =
  | Extract<Size, 'lg' | 'md' | 'sm' | 'xs'>
  | null
  | undefined

type HeadingProps = {
  variant: Extract<TypographyVariant, 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'>
  size?: TypographySize
}

type BodyProps = {
  variant: Extract<TypographyVariant, 'body'>
  size: TypographySize
}

type CaptionProps = {
  variant: Extract<TypographyVariant, 'caption'>
  size: never
}

type DescriptionProps = {
  variant: Extract<TypographyVariant, 'description'>
  size: Extract<TypographySize, 'lg' | 'sm'>
}

export type TypographyProps =
  | HeadingProps
  | BodyProps
  | CaptionProps
  | DescriptionProps

const typographyStyles = cva('typography', {
  variants: {
    variant: {
      h1: 'heading typography-h1',
      h2: 'heading typography-h2',
      h3: 'heading typography-h3',
      h4: 'heading typography-h4',
      h5: 'heading typography-h5',
      h6: 'heading typography-h6',
      body: 'typography-body',
      caption: 'typography-caption',
      description: 'typography-description',
    },
    size: {
      lg: 'typography-size-lg',
      md: 'typography-size-md',
      sm: 'typography-size-sm',
      xs: 'typography-size-xs',
    },
    inverted: {
      true: 'typography-inverted',
    },
  },
  compoundVariants: [
    {
      variant: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
      class: '',
    },
    {
      variant: 'description',
      size: 'lg',
      class: 'typography-description-lg',
    },
    {
      variant: 'description',
      size: 'sm',
      class: 'typography-description-sm',
    },
    {
      variant: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
      inverted: true,
      class: 'typography-heading-inverted',
    },
    {
      variant: 'description',
      inverted: true,
      class: 'typography-description-inverted',
    },
    {
      variant: 'body',
      inverted: true,
      class: 'typography-inverted',
    },
    {
      variant: 'caption',
      inverted: true,
      class: 'typography-caption-inverted',
    },
  ],
})

/**
 * @element dap-ds-typography
 * @summary Typography is used to display text in different styles.
 * @title - Typography
 *
 * @slot default - The content of the typography.
 *
 * @csspart base - The main typography container.
 *
 * @cssproperty --dds-text-color - The color of the typography. (default: var(--dds-text-neutral-base))
 * @cssproperty --dds-text-color-inverted - The color of the typography in inverted mode. (default: var(--dds-text-neutral-on-inverted))
 * @cssproperty --dds-text-heading-color - The color of the heading typography. (default: var(--dds-text-neutral-strong))
 * @cssproperty --dds-text-heading-color-inverted - The color of the heading typography in inverted mode. (default: var(--dds-text-neutral-on-inverted))
 * @cssproperty --dds-text-description-color - The color of the subtle typography. (default: var(--dds-text-neutral-subtle))
 * @cssproperty --dds-text-description-color-inverted - The color of the subtle typography in inverted mode. (default: var(--dds-text-neutral-on-inverted))
 * @cssproperty --dds-text-font-family - The font family of the typography. (default: var(--font-family))
 * @cssproperty --dds-text-font-weight-medium - The font weight of the medium typography. (default: var(--dds-font-weight-medium))
 * @cssproperty --dds-text-font-weight-bold - The font weight of the bold typography. (default: var(--dds-font-weight-bold))
 * @cssproperty --dds-text-font-size-h1 - The font size of the h1 typography. (default: var(--dds-font-7xl))
 * @cssproperty --dds-text-font-size-h2 - The font size of the h2 typography. (default: var(--dds-font-4xl))
 * @cssproperty --dds-text-font-size-h3 - The font size of the h3 typography. (default: var(--dds-font-2xl))
 * @cssproperty --dds-text-font-size-h4 - The font size of the h4 typography. (default: var(--dds-font-xl))
 * @cssproperty --dds-text-font-size-h5 - The font size of the h5 typography. (default: var(--dds-font-lg))
 * @cssproperty --dds-text-font-size-h6 - The font size of the h6 typography. (default: var(--dds-font-base))
 * @cssproperty --dds-text-font-size-body - The font size of the body typography. (default: var(--dds-font-base))
 * @cssproperty --dds-text-font-size-caption - The font size of the caption typography. (default: var(--dds-font-xs))
 * @cssproperty --dds-text-font-size-description - The font size of the description typography. (default: var(--dds-font-base))
 * @cssproperty --dds-text-font-size-description-lg - The font size of the description lg typography. (default: var(--dds-font-base))
 * @cssproperty --dds-text-font-size-description-sm - The font size of the description sm typography. (default: var(--dds-font-sm))
 * @cssproperty --dds-text-font-size-lg - The font size of the lg typography. (default: var(--dds-font-lg))
 * @cssproperty --dds-text-font-size-md - The font size of the md typography. (default: var(--dds-font-base))
 * @cssproperty --dds-text-font-size-sm - The font size of the sm typography. (default: var(--dds-font-sm))
 * @cssproperty --dds-text-font-size-xs - The font size of the xs typography. (default: var(--dds-font-xs))
 */
export default class DapDSTypography
  extends DdsElement
  implements Common<TypographyProps>
{
  static tagName = 'dap-ds-typography'

  /**
   * The variant of the typography.
   * @type {'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body' | 'caption' | 'description'}
   */
  @property({ reflect: true }) variant!: TypographyVariant
  /**
   * The size of the typography.
   * @type {'xs' | 'sm' | 'md' | 'lg'}
   */
  @property({ reflect: true }) size?: TypographySize
  /**
   * The html element of the body typography.
   */
  @property({ reflect: true }) bodyAs?: string
  /** The custom class of the typography. */
  @property() customClass?: string
  /** Whether the typography is an anchor for the TOC component. */
  @property({ type: Boolean, reflect: true }) anchor = false
  /** Whether the typography is inverted. */
  @property({ type: Boolean, reflect: true }) inverted = false

  static readonly styles = unsafeCSS(styles)

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)
    if (this.elementId) {
      this.setAttribute('id', this.elementId)
    }
  }

  private get elementId() {
    return this.id ? this.id : undefined
  }

  private _renderHeading(
    elementId: string | undefined,
    customClass: string,
    variant: TypographyVariant,
    size: TypographySize,
    inverted: boolean,
  ) {
    return html`
      <${unsafeStatic(variant)}
        part="base"
        id=${ifDefined(elementId)}
        class=${clsx(
          typographyStyles({ variant, size, inverted }),
          { [anchorClass]: this.anchor },
          customClass,
        )}
      >
       <slot></slot>
      </${unsafeStatic(variant)}>
    `
  }

  private _renderBody(
    elementId: string | undefined,
    customClass: string,
    size: TypographySize,
    bodyAs: string,
    inverted: boolean,
  ) {
    return html`
      <${unsafeStatic(bodyAs)}
        id=${ifDefined(elementId)}
        part="base"
        class=${clsx(typographyStyles({ variant: 'body', size, inverted }), customClass)}
      >
        <slot></slot>
      </${unsafeStatic(bodyAs)}>
    `
  }

  private _renderCaption(
    elementId: string | undefined,
    customClass: string,
    inverted: boolean,
  ) {
    return html`
      <span
        id=${ifDefined(elementId)}
        part="base"
        class=${clsx(
          typographyStyles({ variant: 'caption', inverted }),
          customClass,
        )}>
        <slot></slot>
      </span>
    `
  }

  private _renderDescription(
    elementId: string | undefined,
    customClass: string,
    size: TypographySize,
    inverted: boolean,
  ) {
    return html`
      <span
        id=${ifDefined(elementId)}
        part="base"
        class=${clsx(
          typographyStyles({ variant: 'description', size, inverted }),
          customClass,
        )}>
        <slot></slot>
      </span>
    `
  }

  private _getElement(
    elementId: string | undefined,
    variant: TypographyVariant,
    size: TypographySize,
    customClass: string = '',
    bodyAs: string = 'p',
    inverted: boolean = false,
  ) {
    switch (variant) {
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
        return this._renderHeading(
          elementId,
          customClass,
          variant,
          size,
          inverted,
        )
      case 'body':
        return this._renderBody(elementId, customClass, size, bodyAs, inverted)
      case 'caption':
        return this._renderCaption(elementId, customClass, inverted)
      case 'description':
        return this._renderDescription(elementId, customClass, size, inverted)
      default:
        return null
    }
  }

  render() {
    return this._getElement(
      this.elementId,
      this.variant,
      this.size,
      this.customClass,
      this.bodyAs,
      this.inverted,
    )
  }
}
